/** Gap between an arrow and its caption, between caption rows, card ↔ first row. */
const CAPTION_GAP = 6;
/** Straight run of an arrow below the last caption row before it may bend. */
const CAPTION_TAIL = 10;
/**
 * Root options of the ELK run (exported for tests / tuning).
 *
 * Chosen by measuring all 16 prod graphs with the canvas's real caption sizes
 * (statistics on). Sums over the 16: bbox width / horizontal arrow length /
 * bends / crossings —
 *   first version (caption zone in the card node, BK BALANCED, gap 48):
 *     13 195 / 13 961 / 228 / 5;
 *   BK `fixedAlignment: NONE`: 13 009 / 13 478 / 116 / 5;
 *   + caption block as its own node: 11 980 / 12 024 / 116 / 5;
 *   + `spacing.nodeNode` 32: 11 612 / 11 224 / 116 / 5;
 *   + layering LONGEST_PATH_SOURCE: 11 720 / 11 506 / 120 / 5  ← this.
 * The last step costs 1 % of width for a guarantee: every step goes to the
 * first layer its arrows allow (what `layout.ts` does), so a caption block —
 * whose only predecessor is its card — always lands in the layer right under
 * the card. Network-simplex layering (ELK's default) is free to push a block
 * further down when that shortens the arrow below it (prod `refund_c2`: the
 * captions of step 7 ended up 137 px under the card); edge priorities do not
 * stop it.
 * Tried and rejected: NETWORK_SIMPLEX placement (14 432 wide), LINEAR_SEGMENTS
 * (241 bends), layering COFFMAN_GRAHAM / LONGEST_PATH (wider, more
 * crossings), `considerModelOrder` and `semiInteractive` (no change), ELK's
 * own centre edge labels in the tail layer (a caption then follows the
 * arrow's LAST segment — next to the target, away from its step).
 * What is left of the width is the captions themselves: two wide outcome
 * captions under one card (`refund_c2` step 3) are ~850 px side by side.
 */
export const ELK_OPTIONS = (size) => ({
    'elk.algorithm': 'layered',
    'elk.direction': 'DOWN',
    'elk.edgeRouting': 'ORTHOGONAL',
    'elk.padding': '[top=0,left=0,bottom=0,right=0]',
    'elk.layered.layering.strategy': 'LONGEST_PATH_SOURCE',
    'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
    'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
    'elk.layered.nodePlacement.bk.fixedAlignment': 'NONE',
    'elk.layered.nodePlacement.favorStraightEdges': 'true',
    'elk.spacing.nodeNode': String(Math.round(size.gapX * (2 / 3))),
    // Every card is followed by its caption block's layer, so the gap between
    // two layers is small; the block itself is the visible space under a card.
    'elk.layered.spacing.nodeNodeBetweenLayers': '8',
    'elk.spacing.edgeNode': '20',
    'elk.layered.spacing.edgeNodeBetweenLayers': '20',
    'elk.spacing.edgeEdge': '14',
    'elk.layered.spacing.edgeEdgeBetweenLayers': '14',
    'elk.layered.mergeEdges': 'false',
    'elk.separateConnectedComponents': 'false',
    'elk.randomSeed': '1',
});
/**
 * Ports and captions of one step's exits, relative to the card's top-left
 * (pure; exported for tests).
 *
 * Ports are spread over the card's bottom: exit i of n at `(i + ½) / n` of
 * the width. Captions: the first exit's stands LEFT of its arrow, the last
 * one's RIGHT of it (so neither crosses a neighbour's arrow), a lone exit's
 * right of it; middle exits' captions sit ON their arrow, a row lower. A
 * caption that would touch one already placed moves down a row.
 */
export const exitCaptions = (width, height, sizes) => {
    const n = sizes.length;
    // Whole pixels: ELK snaps node positions to integers, and a fractional
    // port would leave the arrow's first segment a fraction off vertical.
    const portX = sizes.map((_, i) => Math.round((width * (i + 0.5)) / n));
    const captions = [];
    const placed = [];
    const rowH = Math.max(0, ...sizes.map((s) => s?.height ?? 0)) + CAPTION_GAP;
    sizes.forEach((s, i) => {
        if (s === null) {
            captions.push(null);
            return;
        }
        const align = n === 1 || i === n - 1 ? 'start' : i === 0 ? 'end' : 'center';
        const x = align === 'start'
            ? portX[i] + CAPTION_GAP
            : align === 'end'
                ? portX[i] - CAPTION_GAP - s.width
                : portX[i] - s.width / 2;
        let row = align === 'center' ? 1 : 0;
        const at = (r) => ({
            x,
            y: height + CAPTION_GAP + r * rowH,
            width: s.width,
            height: s.height,
        });
        const touches = (r) => placed.some((p) => r.x < p.x + p.width + CAPTION_GAP &&
            p.x < r.x + r.width + CAPTION_GAP &&
            r.y < p.y + p.height &&
            p.y < r.y + r.height);
        while (touches(at(row)))
            row++;
        const rect = at(row);
        placed.push(rect);
        captions.push({ ...rect, align });
    });
    const bottom = Math.max(height, ...placed.map((p) => p.y + p.height));
    return {
        portX,
        captions,
        zoneHeight: placed.length > 0 ? bottom - height + CAPTION_TAIL : 0,
    };
};
const nodeKey = (id) => `node:${id}`;
const endKey = (id) => `end:${id}`;
const jumpKey = (id) => `jump:${id}`;
const inPort = (key) => `${key}|in`;
const outPort = (exitId) => `out|${exitId}`;
const BLOCK = 'block>';
/**
 * The ELK input graph of the members of one band (main flow or unreachable).
 * Pure — exported so tests can check the mapping without running ELK.
 */
export const buildElkGraph = (view, size, members, opts = {}) => buildBand(view, size, members, opts).graph;
const buildBand = (view, size, members, opts) => {
    const children = [];
    const edges = [];
    const frames = new Map();
    /** Exits that leave through a caption block: ELK routes them from its bottom. */
    const viaBlock = new Set();
    const port = (id, x, y, side) => ({
        id,
        x,
        y,
        width: 0,
        height: 0,
        layoutOptions: { 'elk.port.side': side },
    });
    /** A card as an ELK node, plus the caption block of its exits if they have captions. */
    const box = (key, width, height, outs) => {
        const { portX, captions, zoneHeight } = exitCaptions(width, height, outs.map((id) => opts.labelSizes?.[id] ?? null));
        const frame = { width, height, captions: new Map(), portX: new Map(), block: null };
        outs.forEach((id, i) => {
            frame.portX.set(id, portX[i]);
            const c = captions[i];
            if (c)
                frame.captions.set(id, c);
        });
        frames.set(key, frame);
        children.push({
            id: key,
            width,
            height,
            layoutOptions: { 'elk.portConstraints': 'FIXED_POS' },
            ports: [
                port(inPort(key), Math.round(width / 2), 0, 'NORTH'),
                ...outs.map((exitId, i) => port(outPort(exitId), portX[i], height, 'SOUTH')),
            ],
        });
        if (zoneHeight === 0)
            return;
        // The block reaches as far as the captions stick out of the card: left
        // for the first exit's, right for the last one's.
        const left = Math.ceil(Math.max(0, ...captions.map((c) => (c ? -c.x : 0))));
        const right = Math.ceil(Math.max(0, ...captions.map((c) => (c ? c.x + c.width - width : 0))));
        const id = `${BLOCK}${key}`;
        frame.block = { id, left };
        children.push({
            id,
            width: left + width + right,
            height: zoneHeight,
            layoutOptions: { 'elk.portConstraints': 'FIXED_POS' },
            ports: outs.flatMap((exitId, i) => [
                port(`${id}|in|${exitId}`, left + portX[i], 0, 'NORTH'),
                port(`${id}|out|${exitId}`, left + portX[i], zoneHeight, 'SOUTH'),
            ]),
        });
        for (const exitId of outs) {
            edges.push({
                id: `lead|${exitId}`,
                sources: [outPort(exitId)],
                targets: [`${id}|in|${exitId}`],
                layoutOptions: {
                    // Straight down: the captions belong right under the card.
                    'elk.layered.priority.straightness': '100',
                },
            });
            viaBlock.add(exitId);
        }
    };
    const sourcePort = (exitId, blockId) => blockId !== null && viaBlock.has(exitId) ? `${blockId}|out|${exitId}` : outPort(exitId);
    // Exits of every node in slot order (edges, jumps and ends together).
    const exitsOf = new Map();
    const slotIndex = new Map();
    for (const n of view.nodes)
        n.slots.forEach((s, i) => slotIndex.set(`${n.id}/${s.key}`, i));
    for (const e of [...view.edges, ...view.jumps, ...view.ends]) {
        const list = exitsOf.get(e.source) ?? [];
        list.push({ id: e.id, slot: slotIndex.get(`${e.source}/${e.slotKeys[0]}`) ?? 0 });
        exitsOf.set(e.source, list);
    }
    for (const list of exitsOf.values())
        list.sort((a, b) => a.slot - b.slot);
    // Back edges of cycles: DFS over arrows in slot order from the start (the
    // reachable flow), then from every step it did not visit, in step-number
    // order (cycles among unreachable steps). See the mapping notes above.
    const back = new Set();
    {
        const state = new Map();
        const dfs = (id) => {
            state.set(id, 'open');
            for (const { id: exitId } of exitsOf.get(id) ?? []) {
                const e = view.edges.find((x) => x.id === exitId);
                if (!e)
                    continue;
                const s = state.get(e.target);
                if (s === 'open')
                    back.add(e.id);
                else if (s === undefined)
                    dfs(e.target);
            }
            state.set(id, 'done');
        };
        for (const id of [view.entryNodeId, ...view.nodes.map((n) => n.id)]) {
            if (view.numberOf[id] !== undefined && !state.has(id))
                dfs(id);
        }
    }
    for (const n of view.nodes) {
        if (!members.has(n.id))
            continue;
        const outs = (exitsOf.get(n.id) ?? []).map((x) => x.id);
        box(nodeKey(n.id), size.nodeWidth, size.nodeHeights?.[n.id] ?? size.nodeHeight, outs);
    }
    for (const n of view.nodes) {
        if (!members.has(n.id))
            continue;
        const blockId = frames.get(nodeKey(n.id)).block?.id ?? null;
        for (const { id } of exitsOf.get(n.id) ?? []) {
            const edge = view.edges.find((e) => e.id === id);
            if (edge) {
                // Into another band, or closing a cycle: no route.
                if (!members.has(edge.target) || back.has(id))
                    continue;
                edges.push({
                    id,
                    sources: [sourcePort(id, blockId)],
                    targets: [inPort(nodeKey(edge.target))],
                });
                continue;
            }
            const jump = view.jumps.find((j) => j.id === id);
            const key = jump ? jumpKey(id) : endKey(id);
            box(key, size.stubWidth, size.stubHeight, []);
            edges.push({ id, sources: [sourcePort(id, blockId)], targets: [inPort(key)] });
        }
    }
    return {
        graph: {
            id: 'root',
            layoutOptions: { ...ELK_OPTIONS(size), ...opts.elkOptions },
            children,
            edges,
        },
        frames,
    };
};
/** Reads ELK's answer back into view terms (ELK root coordinates). */
const readBand = (out, frames) => {
    const layout = {
        nodes: {},
        ends: {},
        jumps: {},
        layerOf: {},
        routes: {},
        width: 0,
        height: 0,
    };
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const grow = (r) => {
        minX = Math.min(minX, r.x);
        minY = Math.min(minY, r.y);
        maxX = Math.max(maxX, r.x + r.width);
        maxY = Math.max(maxY, r.y + r.height);
    };
    const cards = new Map();
    const blocks = new Map();
    const tops = [];
    for (const c of out.children ?? []) {
        const r = { x: c.x ?? 0, y: c.y ?? 0, width: c.width ?? 0, height: c.height ?? 0 };
        grow(r);
        if (c.id.startsWith(BLOCK)) {
            blocks.set(c.id, { x: r.x, y: r.y });
            continue;
        }
        cards.set(c.id, r);
        const sep = c.id.indexOf(':');
        const kind = c.id.slice(0, sep);
        const id = c.id.slice(sep + 1);
        if (kind === 'node') {
            layout.nodes[id] = r;
            tops.push(r.y);
        }
        else if (kind === 'end')
            layout.ends[id] = r;
        else
            layout.jumps[id] = r;
    }
    // `layerOf` (debug / tests): rank of the card's top among distinct tops.
    const ranks = [...new Set(tops)].sort((a, b) => a - b);
    for (const [id, r] of Object.entries(layout.nodes))
        layout.layerOf[id] = ranks.indexOf(r.y);
    const sourceOf = new Map();
    for (const [key, f] of frames)
        for (const exitId of f.portX.keys())
            sourceOf.set(exitId, key);
    const sectionPoints = (e) => {
        const sec = e.sections?.[0];
        return sec ? [sec.startPoint, ...(sec.bendPoints ?? []), sec.endPoint] : [];
    };
    const leads = new Map();
    for (const e of (out.edges ?? [])) {
        if (e.id.startsWith('lead|'))
            leads.set(e.id.slice('lead|'.length), sectionPoints(e));
    }
    for (const e of (out.edges ?? [])) {
        if (e.id.startsWith('lead|'))
            continue;
        const main = sectionPoints(e);
        if (main.length === 0)
            continue;
        const srcKey = sourceOf.get(e.id);
        const card = cards.get(srcKey);
        const f = frames.get(srcKey);
        // Card bottom → (through its caption block) → the target's top.
        const start = { x: card.x + f.portX.get(e.id), y: card.y + card.height };
        const points = simplify([start, ...(leads.get(e.id) ?? []), ...main]);
        for (const p of points)
            grow({ x: p.x, y: p.y, width: 0, height: 0 });
        const c = f.captions.get(e.id);
        const block = f.block ? blocks.get(f.block.id) : undefined;
        // Captions are laid out card-relative; the block may sit a few px off the
        // card if ELK could not align it, and the captions belong to the block.
        layout.routes[e.id] = {
            points,
            label: c && block && f.block
                ? { ...c, x: block.x + f.block.left + c.x, y: block.y + c.y - f.height }
                : null,
        };
    }
    return { layout, minX, minY, maxX, maxY };
};
/** Drops repeated points and the middle of three collinear ones (real bends only). */
const simplify = (pts) => {
    const out = [];
    for (const p of pts) {
        const q = { x: p.x, y: p.y };
        const last = out.at(-1);
        if (last && last.x === q.x && last.y === q.y)
            continue;
        const prev = out.at(-2);
        if (prev &&
            last &&
            ((prev.x === last.x && last.x === q.x) || (prev.y === last.y && last.y === q.y))) {
            out[out.length - 1] = q;
            continue;
        }
        out.push(q);
    }
    return out;
};
const translate = (band, dx, dy, into) => {
    const mv = (r) => ({ ...r, x: r.x + dx, y: r.y + dy });
    for (const [k, r] of Object.entries(band.nodes))
        into.nodes[k] = mv(r);
    for (const [k, r] of Object.entries(band.ends))
        into.ends[k] = mv(r);
    for (const [k, r] of Object.entries(band.jumps))
        into.jumps[k] = mv(r);
    for (const [k, route] of Object.entries(band.routes)) {
        into.routes[k] = {
            points: route.points.map((p) => ({ x: p.x + dx, y: p.y + dy })),
            label: route.label ? mv(route.label) : null,
        };
    }
};
/**
 * ELK's chunk could not be loaded (offline, a deploy replaced the chunk). Not
 * a fact about the graph: the next layout tries to load it again.
 */
export class ElkUnavailableError extends Error {
    constructor(cause) {
        super('ELK layout engine could not be loaded', { cause });
        this.name = 'ElkUnavailableError';
    }
}
let elkPromise = null;
/** One ELK instance per page, created on first use (lazy chunk). */
const loadElk = () => {
    if (elkPromise === null) {
        const p = import('elkjs/lib/elk.bundled.js').then((m) => new m.default(), (cause) => {
            throw new ElkUnavailableError(cause);
        });
        // A failed chunk load must not poison every later attempt.
        p.catch(() => {
            if (elkPromise === p)
                elkPromise = null;
        });
        elkPromise = p;
    }
    return elkPromise;
};
/** Start loading ELK ahead of the first layout. */
export const preloadElk = () => {
    loadElk().catch(() => { });
};
/**
 * The three bands of a view, in drawing order: the main flow, the go-to
 * targets with everything below them (same rule as `layout.ts`), the steps
 * the start does not reach.
 */
const bandsOf = (view) => {
    const unreachable = new Set(view.unreachable);
    const reachable = new Set(view.nodes.map((n) => n.id).filter((id) => !unreachable.has(id)));
    const pinned = new Set();
    const stack = view.jumps.map((j) => j.target).filter((t) => reachable.has(t));
    while (stack.length > 0) {
        const id = stack.pop();
        if (pinned.has(id) || !reachable.has(id))
            continue;
        pinned.add(id);
        for (const e of view.edges)
            if (e.source === id)
                stack.push(e.target);
    }
    const main = new Set([...reachable].filter((id) => !pinned.has(id)));
    return [main, pinned, unreachable];
};
/**
 * A key equal for two calls exactly when {@link layoutViewElk} gives them the
 * same layout: built from EVERYTHING the layout reads — the ELK input graph of
 * every band (cards, their ports in slot order, caption blocks, stubs,
 * arrows), the caption boxes, the go-to labels the hub band centres under,
 * the band gaps. A cache keyed by it can never hand out a layout of another
 * shape (e.g. two outcome groups swapped between a step and an end: same ids,
 * same sizes, other ports).
 */
export const layoutKey = (view, size, opts = {}) => JSON.stringify({
    bands: bandsOf(view).map((members) => members.size === 0 ? null : buildBand(view, size, members, opts).graph),
    labelSizes: opts.labelSizes ?? null,
    jumps: view.jumps.map((j) => [j.id, j.target]),
    gapY: size.gapY,
    bandGapY: size.bandGapY ?? null,
});
/**
 * Lays out the view with ELK. Rejects only if ELK cannot be loaded or fails;
 * the caller decides on a fallback (`layoutView`).
 */
export const layoutViewElk = async (view, size, opts = {}) => {
    const elk = await loadElk();
    const [main, pinned, unreachable] = bandsOf(view);
    const run = async (members) => {
        if (members.size === 0)
            return null;
        const { graph, frames } = buildBand(view, size, members, opts);
        return readBand(await elk.layout(graph), frames);
    };
    const bands = [await run(main), await run(pinned), await run(unreachable)];
    const out = {
        nodes: {},
        ends: {},
        jumps: {},
        layerOf: {},
        routes: {},
        width: 0,
        height: 0,
    };
    let y = 0;
    let layerBase = 0;
    let placedAny = false;
    bands.forEach((band, i) => {
        if (band === null)
            return;
        if (placedAny)
            y += i === 2 ? (size.bandGapY ?? size.gapY) : Math.round(size.gapY * 0.5);
        let dx = -band.minX;
        if (i === 1 && placedAny) {
            // The hub band: centred under the go-to labels that point into it.
            const centres = view.jumps
                .map((j) => out.jumps[j.id])
                .filter((r) => r !== undefined)
                .map((r) => r.x + r.width / 2);
            if (centres.length > 0) {
                const want = centres.reduce((a, b) => a + b, 0) / centres.length;
                dx = want - (band.minX + band.maxX) / 2;
            }
        }
        translate(band.layout, dx, y - band.minY, out);
        for (const [k, l] of Object.entries(band.layout.layerOf))
            out.layerOf[k] = l + layerBase;
        layerBase += Math.max(-1, ...Object.values(band.layout.layerOf)) + 1;
        y += band.maxY - band.minY;
        placedAny = true;
    });
    // Normalise: the leftmost box or caption at x = 0.
    const xs = [];
    const rights = [];
    const all = [
        ...Object.values(out.nodes),
        ...Object.values(out.ends),
        ...Object.values(out.jumps),
        ...Object.values(out.routes).flatMap((r) => (r.label ? [r.label] : [])),
    ];
    for (const r of all) {
        xs.push(r.x);
        rights.push(r.x + r.width);
    }
    for (const r of Object.values(out.routes)) {
        for (const p of r.points) {
            xs.push(p.x);
            rights.push(p.x);
        }
    }
    const left = xs.length > 0 ? Math.min(...xs) : 0;
    const moved = { ...out, nodes: {}, ends: {}, jumps: {}, routes: {} };
    translate(out, -left, 0, moved);
    moved.width = rights.length > 0 ? Math.max(...rights) - left : 0;
    moved.height = y;
    return moved;
};
//# sourceMappingURL=layout-elk.js.map